import React, { ReactNode } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import SearchableSelect from '../SearchableSelect/SearchableSelect';
import { UserOutlineIcon } from '../../icons/inputIcons';
import { IGrowerItem } from '../../../modules/API/Defaults/models/growers';

interface Props {
    growers: IGrowerItem[];
    value: IGrowerItem | null;
    onSelect: (grower: IGrowerItem | null) => void;
    placeholder?: string;
    disabled?: boolean;
    readOnly?: boolean;
    allowClear?: boolean;
    clearLabel?: string;
    leadingIcon?: ReactNode | null;
    className?: string;
}

const GrowerSelector: React.FC<Props> = ({
    growers,
    value,
    onSelect,
    placeholder,
    disabled,
    readOnly,
    allowClear,
    clearLabel,
    leadingIcon,
    className,
}) => {
    const [t] = useTranslation('selectors');

    return (
        <SearchableSelect<IGrowerItem>
            items={growers}
            value={value}
            onSelect={onSelect}
            getKey={(g) => g.GrowerID}
            getLabel={(g) => (g.GrowerName ?? '').trim()}
            getSearchText={(g) => `${g.GrowerName ?? ''} ${g.GrowerID}`}
            renderItem={(g) => (
                <Row>
                    <GrowerName>{(g.GrowerName ?? '').trim()}</GrowerName>
                    <GrowerCode>{g.GrowerID}</GrowerCode>
                </Row>
            )}
            placeholder={placeholder ?? t('selectors_grower_placeholder')}
            disabled={disabled}
            readOnly={readOnly}
            allowClear={allowClear}
            clearLabel={clearLabel ?? t('selectors_clear')}
            leadingIcon={leadingIcon === undefined ? <UserOutlineIcon /> : leadingIcon}
            className={className}
            emptyLabel={t('selectors_grower_empty')}
        />
    );
};

export default GrowerSelector;

const Row = styled.span`
    display: inline-flex;
    align-items: baseline;
    gap: 0.5rem;
    min-width: 0;
`;

const GrowerName = styled.span`
    color: ${({ theme }) => theme.colors.ink};
    font-weight: 500;
`;

const GrowerCode = styled.span`
    color: ${({ theme }) => theme.colors.ink3};
    font-weight: 400;
`;
